import React from "react";
import { useNavigate } from "react-router-dom";

import { useInfiniteQuery } from "@tanstack/react-query";
import moment from "moment";
import clsx from "clsx";
import { get, isNil } from "lodash";

import Skeleton from "@/components/common/Skeleton";
import LazyImage from "@/components/common/LazyImage";

import { getUserSocialListings } from "@/services/UserService";
import useLocalStorage from "@/hooks/useLocalStorage";

const ITEMS_PER_PAGE = 10;
const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const CACHE_TIME = 30 * 60 * 1000; // 30 minutes

interface Score {
    points: number;
}

interface UserGuess {
    created_at: string;
    Score: Score;
}

interface SocialProperty {
    id: string;
    full_address: string;
    image_urls: string[];
    status_indicator: "Active" | "Contract" | "Closed";
    UserGuesses: UserGuess[];
}

interface PropertiesPage {
    data: SocialProperty[];
    nextPage?: number;
}

interface UserPropertiesProps {
    userId: string;
    isOwnProfile: boolean;
}

const formatAddress = (address: string): string => {
    return address.toLowerCase().replace(/[^a-z0-9]+/g, "-");
};

const PropertySkeleton = () => (
    <div className="tw-flex tw-items-center tw-gap-4 tw-p-4 tw-rounded-lg tw-border tw-border-gray-200">
        <Skeleton className="tw-h-20 tw-w-20 tw-rounded-lg" />
        <div className="tw-flex tw-flex-col tw-gap-2 tw-flex-1">
            <Skeleton className="tw-h-4 tw-w-3/4" />
            <Skeleton className="tw-h-3 tw-w-1/2" />
            <Skeleton className="tw-h-3 tw-w-1/4" />
        </div>
    </div>
);

/**
 * UserProperties component that displays a user's social listings with a Load More button
 * @param {Object} props - Component props
 * @param {string} props.userId - The ID of the user whose listings are being displayed
 * @param {boolean} props.isOwnProfile - Whether the listings are being displayed on the user's own profile
 * @returns {JSX.Element} UserProperties component
 */
const UserProperties: React.FC<UserPropertiesProps> = ({ userId, isOwnProfile }) => {
    const navigate = useNavigate();
    const [socialState, setSocialState] = useLocalStorage("social-state", {});

    const {
        data: propertiesData,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = useInfiniteQuery<PropertiesPage>({
        queryKey: ["userProperties", userId],
        queryFn: ({ pageParam = 1 }) => getUserSocialListings(userId, pageParam as number, ITEMS_PER_PAGE),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            if (!lastPage.data || lastPage.data.length < ITEMS_PER_PAGE) {
                return undefined;
            }
            return lastPage.nextPage;
        },
        enabled: !!userId,
        staleTime: STALE_TIME,
        gcTime: CACHE_TIME,
    });

    const handlePropertyClick = (fullAddress: string, property: SocialProperty) => {
        if (!fullAddress) return;
        const formattedAddress = formatAddress(fullAddress);
        setSocialState({ ...(socialState as Record<string, any>), property });
        navigate(`/social/listing/${formattedAddress}`);
    };

    if (isLoading) {
        return (
            <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
                {[...Array(3)].map((_, index) => (
                    <PropertySkeleton key={index} />
                ))}
            </div>
        );
    }

    const allProperties = propertiesData?.pages.flatMap((page) => (page as PropertiesPage).data) ?? [];

    if (allProperties.length === 0) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-p-4 tw-text-gray-500">
                {isOwnProfile ? "You haven't guessed any properties yet." : "No properties guessed yet."}
            </div>
        );
    }

    return (
        <div className="tw-space-y-4">
            <div className="tw-grid tw-grid-cols-1 tw-gap-4 tw-mx-4">
                {allProperties.map((property) => (
                    <div
                        key={property.id}
                        className="tw-cursor-pointer tw-bg-white tw-rounded-2xl tw-overflow-hidden tw-shadow-[0_4px_8px_rgba(0,0,0,0.1)] tw-p-4 hover:tw-bg-gray-50"
                        onClick={() => handlePropertyClick(property.full_address, property)}
                    >
                        <div className="tw-flex tw-gap-4">
                            <div className="tw-w-[35%] tw-relative">
                                {property.image_urls?.[0] && (
                                    <LazyImage
                                        src={property.image_urls[0]}
                                        alt={property.full_address}
                                        className="tw-w-full tw-h-[90px] tw-rounded-lg tw-object-cover"
                                    />
                                )}
                                {!isNil(get(property, "UserGuesses.0.Score.points", null)) && (
                                    <div className="tw-absolute tw-top-[-10px] tw-left-[-10px] tw-bg-[#FE8601] tw-text-white tw-rounded-full tw-w-[27px] tw-h-[27px] tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-medium">
                                        +{get(property, "UserGuesses.0.Score.points", 0)}
                                    </div>
                                )}
                            </div>
                            <div className="tw-w-[65%]">
                                <div className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-m-0">
                                    <h3 className="tw-text-[14px] tw-font-merriweather tw-font-bold tw-text-black tw-line-clamp-3">
                                        {property.full_address}
                                    </h3>
                                    <span
                                        className={clsx(
                                            "tw-px-3 tw-py-1 tw-text-[14px] tw-font-noto tw-font-bold tw-rounded-full tw-text-white",
                                            {
                                                "tw-bg-[#AAD880]": property?.status_indicator === "Active",
                                                "tw-bg-[#FDD56F]": property?.status_indicator === "Contract",
                                                "tw-bg-[#D9D9D9]": property?.status_indicator === "Closed",
                                            },
                                        )}
                                    >
                                        {property?.status_indicator ?? ""}
                                    </span>
                                </div>
                                <p className="tw-m-0 tw-font-noto tw-text-[14px] tw-text-[#666666]">
                                    Guessed on{" "}
                                    {moment(get(property, "UserGuesses.0.created_at", "")).format("MMMM DD, YYYY")}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {hasNextPage && (
                <button
                    className="tw-mt-4 tw-rounded-lg tw-bg-gray-100 tw-px-4 tw-py-2 tw-text-sm tw-font-medium hover:tw-bg-gray-200"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                >
                    {isFetchingNextPage ? "Loading..." : "Load More"}
                </button>
            )}
        </div>
    );
};

export default UserProperties;
